import { useState, useEffect, useCallback } from 'react';
import { useAdmin } from '@/contexts/AdminContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import logger from '@/utils/logger';
import {
  Lock, Shield, Trash2, RefreshCw, Eye, EyeOff, AlertTriangle,
  CheckCircle2, Clock, Globe
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AdminSecurity = () => {
  const { getAuthHeaders, admin } = useAdmin();
  const [currentPw, setCurrentPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirmPw, setConfirmPw] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [changingPw, setChangingPw] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [confirmReset, setConfirmReset] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const fetchAudit = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/login-audit`, {
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      setAuditLogs(data.logs || []);
    } catch (err) {
      logger.error('Failed to fetch audit logs:', err);
    } finally {
      setLoadingLogs(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchAudit();
  }, [fetchAudit]);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPw.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setChangingPw(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/change-password`, {
        method: 'PUT',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: currentPw, new_password: newPw }),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('Password changed successfully');
        setCurrentPw('');
        setNewPw('');
        setConfirmPw('');
      } else {
        toast.error(data.detail || 'Failed to change password');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setChangingPw(false);
    }
  };

  const handleResetData = async () => {
    setResetting(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/reset-all-data`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      const data = await res.json();
      if (res.ok) {
        toast.success('All demo data has been cleared');
        setConfirmReset(false);
      } else {
        toast.error(data.detail || 'Failed to reset data');
      }
    } catch {
      toast.error('Network error');
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="space-y-8 max-w-3xl" data-testid="admin-security">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">Security Settings</h1>
        <p className="text-slate-500">Manage your password and security preferences</p>
      </div>

      {/* Password Change */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Lock size={18} className="text-blue-600" />
            Change Password
          </CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4 max-w-sm">
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Current Password</Label>
              <div className="relative">
                <Input
                  type={showPw ? 'text' : 'password'}
                  value={currentPw}
                  onChange={(e) => setCurrentPw(e.target.value)}
                  required
                  data-testid="current-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">New Password</Label>
              <Input
                type={showPw ? 'text' : 'password'}
                value={newPw}
                onChange={(e) => setNewPw(e.target.value)}
                minLength={8}
                required
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-sm text-slate-600">Confirm New Password</Label>
              <Input
                type={showPw ? 'text' : 'password'}
                value={confirmPw}
                onChange={(e) => setConfirmPw(e.target.value)}
                minLength={8}
                required
                data-testid="confirm-password-input"
              />
              {confirmPw && newPw !== confirmPw && (
                <p className="text-xs text-red-500">Passwords do not match</p>
              )}
            </div>
            <Button
              type="submit"
              disabled={changingPw || !currentPw || !newPw || newPw !== confirmPw}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="change-password-btn"
            >
              {changingPw ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Changing...</> : 'Change Password'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* Security Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Shield size={18} className="text-green-600" />
            Security Protection
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-3 text-sm">
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
              <div>
                <strong className="text-green-800">Brute Force Protection</strong>
                <p className="text-green-600">5 failed attempts → 30 min lockout. 10 attempts → permanent lock with email verification.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
              <div>
                <strong className="text-green-800">Email Verification</strong>
                <p className="text-green-600">6-digit code sent to registered email to unlock permanently locked accounts.</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
              <CheckCircle2 size={16} className="text-green-500 mt-0.5 shrink-0" />
              <div>
                <strong className="text-green-800">Login Audit Trail</strong>
                <p className="text-green-600">Every login attempt (success & failure) is logged with IP address and timestamp.</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login Audit */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock size={18} className="text-slate-600" />
            Recent Login Activity
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loadingLogs ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-slate-300" />
            </div>
          ) : auditLogs.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-8">No login activity recorded yet</p>
          ) : (
            <div className="space-y-2 max-h-72 overflow-y-auto">
              {auditLogs.slice(0, 20).map((log, i) => (
                <div
                  key={`${log.timestamp}-${i}`}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm ${
                    log.success ? 'bg-green-50' : 'bg-red-50'
                  }`}
                  data-testid={`audit-log-${i}`}
                >
                  <div className={`w-2 h-2 rounded-full shrink-0 ${log.success ? 'bg-green-500' : 'bg-red-500'}`} />
                  <Globe size={14} className="text-slate-400 shrink-0" />
                  <span className="text-slate-500 font-mono text-xs">{log.ip}</span>
                  <span className="text-slate-700 flex-1">{log.email}</span>
                  <span className={`text-xs font-medium ${log.success ? 'text-green-600' : 'text-red-600'}`}>
                    {log.success ? 'Success' : log.detail || 'Failed'}
                  </span>
                  <span className="text-xs text-slate-400">
                    {new Date(log.timestamp).toLocaleString()}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Data Reset */}
      <Card className="border-red-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg text-red-600">
            <Trash2 size={18} />
            Reset All Data
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-500 mb-4">
            This will permanently delete all packages, destinations, theme tours, enquiries, leads, and contacts.
            Admin accounts, site pages, and Meta Ads configuration will be preserved.
          </p>
          {!confirmReset ? (
            <Button
              variant="outline"
              className="border-red-300 text-red-600 hover:bg-red-50"
              onClick={() => setConfirmReset(true)}
              data-testid="reset-data-btn"
            >
              <Trash2 size={16} className="mr-2" />
              Reset All Demo Data
            </Button>
          ) : (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-2">
                <AlertTriangle className="text-red-500 shrink-0 mt-0.5" size={18} />
                <p className="text-sm text-red-700 font-medium">
                  Are you sure? This action cannot be undone. All tours, enquiries, leads, and contacts will be permanently deleted.
                </p>
              </div>
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setConfirmReset(false)}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleResetData}
                  disabled={resetting}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="confirm-reset-btn"
                >
                  {resetting ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Deleting...</> : 'Yes, Delete Everything'}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecurity;
